import { fetchDay, fetchLocations, fetchTides } from "./api.js";
import { populateLocationPicker } from "./components/locationPicker.js";
import { renderTideTable } from "./components/tideTable.js";
import { renderDayView } from "./components/dayView.js";

const listView = document.getElementById("list-view") as HTMLElement;
const dayView = document.getElementById("day-view") as HTMLElement;
const controls = document.querySelector(".controls") as HTMLElement;
const locationPicker = document.getElementById("location-picker") as HTMLSelectElement;
const daysPicker = document.getElementById("days-picker") as HTMLSelectElement;
const weekendsOnly = document.getElementById("weekends-only") as HTMLInputElement;
const refreshBtn = document.getElementById("refresh-btn") as HTMLButtonElement;
const results = document.getElementById("results") as HTMLElement;

type Route =
  | { kind: "list" }
  | { kind: "day"; locationId: string; date: string };

function parseRoute(path: string): Route {
  const m = path.match(/^\/day\/([^/]+)\/(\d{4}-\d{2}-\d{2})\/?$/);
  if (m) {
    return { kind: "day", locationId: decodeURIComponent(m[1]!), date: m[2]! };
  }
  return { kind: "list" };
}

function showRoute(route: Route): void {
  listView.hidden = route.kind !== "list";
  controls.hidden = route.kind !== "list";
  dayView.hidden = route.kind !== "day";
}

async function loadList(): Promise<void> {
  const locationId = locationPicker.value;
  const days = Number(daysPicker.value);
  if (!locationId) return;

  results.setAttribute("aria-busy", "true");
  setListStatus(`Loading ${locationPicker.selectedOptions[0]?.textContent ?? ""}…`);
  try {
    const data = await fetchTides(locationId, days);
    if (weekendsOnly.checked) {
      data.scored = data.scored.filter((t) => t.isWeekend);
    }
    renderTideTable(results, data);
  } catch (err) {
    setListStatus(`Error: ${(err as Error).message}`);
  } finally {
    results.setAttribute("aria-busy", "false");
  }
}

async function loadDay(locationId: string, date: string): Promise<void> {
  dayView.setAttribute("aria-busy", "true");
  setDayStatus("Loading day…");
  try {
    const data = await fetchDay(locationId, date);
    renderDayView(dayView, data);
  } catch (err) {
    setDayStatus(`Error: ${(err as Error).message}`);
  } finally {
    dayView.setAttribute("aria-busy", "false");
    window.scrollTo(0, 0);
  }
}

function setListStatus(msg: string): void {
  results.replaceChildren();
  const p = document.createElement("p");
  p.className = "status";
  p.textContent = msg;
  results.appendChild(p);
}

function setDayStatus(msg: string): void {
  dayView.replaceChildren();
  const p = document.createElement("p");
  p.className = "status";
  p.textContent = msg;
  dayView.appendChild(p);
}

async function render(route: Route): Promise<void> {
  showRoute(route);
  if (route.kind === "day") {
    await loadDay(route.locationId, route.date);
  }
}

function navigate(path: string): void {
  if (path === location.pathname) return;
  history.pushState({}, "", path);
  void render(parseRoute(path));
}

function interceptInAppClicks(): void {
  document.addEventListener("click", (e) => {
    if (e.defaultPrevented) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.button !== 0) return;
    const link = (e.target as HTMLElement | null)?.closest("a");
    if (!link) return;
    if (link.target && link.target !== "_self") return;
    const href = link.getAttribute("href");
    if (!href || !href.startsWith("/") || href.startsWith("//")) return;
    e.preventDefault();
    navigate(href);
  });
}

async function init(): Promise<void> {
  try {
    const locations = await fetchLocations();
    populateLocationPicker(locationPicker, locations);
    locationPicker.addEventListener("change", () => void loadList());
    daysPicker.addEventListener("change", () => void loadList());
    weekendsOnly.addEventListener("change", () => void loadList());
    refreshBtn.addEventListener("click", () => void loadList());
    await loadList();
  } catch (err) {
    setListStatus(`Failed to load locations: ${(err as Error).message}`);
  }

  interceptInAppClicks();
  window.addEventListener("popstate", () => void render(parseRoute(location.pathname)));

  await render(parseRoute(location.pathname));
}

void init();
